import React, { useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles((theme) => ({
  stepperWrapper: {
    width: '100%',
    overflow: 'hidden',
    padding: theme.spacing(5, 0),
  },
  stepperContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  stepCircle: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.3s ease',
  },
  clickable: {
    cursor: 'pointer',
    '&:hover': {
      transform: 'scale(1.1)',
    },
  },
  activeCircle: {
    backgroundColor: theme.palette.primary.main,
  },
  completedCircle: {
    backgroundColor: theme.palette.tertiary['clr-500'],
  },
  pendingCircle: {
    backgroundColor: theme.palette.neutral['clr-300'],
  },
  stepLabel: {
    marginLeft: theme.spacing(1),
    whiteSpace: 'nowrap',
  },
  clickableLabel: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  connector: {
    width: '50px',
    flexShrink: 0,
    borderTop: `2px dashed ${theme.palette.neutral['clr-400']}`,
    margin: theme.spacing(0, 1.5),
  },
  completedConnector: {
    borderTop: `2px dashed ${theme.palette.tertiary['clr-500']}`,
  },
}));

const steps = [
  { _id: 'TEMPLATE', label: 'Template' },
  { _id: 'HEADINGS', label: 'Headings' },
  { _id: 'WORK_EXPERIENCE', label: 'Work Experience' },
  { _id: 'EDUCATION', label: 'Education' },
  { _id: 'SKILLS', label: 'Skills' },
  { _id: 'PREVIEW', label: 'Preview' },
];

const ResumeFormStepper = ({ activeStep = 0, currentStep = 0, onStepClick }) => {
  const classes = useStyles();
  const stepRefs = useRef([]);
  const containerRef = useRef(null);

  useEffect(() => {
    const activeRef = stepRefs.current[currentStep];
    if (activeRef && containerRef.current) {
      const container = containerRef.current;
      const stepLeft = activeRef.offsetLeft;
      const stepWidth = activeRef.offsetWidth;
      const containerWidth = container.offsetWidth;

      const scrollTo = stepLeft - (containerWidth / 2) + (stepWidth / 2);
      container.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  }, [currentStep]);

  const getCircleClass = (index) => {
    if (index === currentStep) return classes.activeCircle;
    if (index <= activeStep) return classes.completedCircle;
    return classes.pendingCircle;
  };

  const isClickable = (index) => index <= activeStep;

  const handleClick = (index) => {
    if (isClickable(index) && onStepClick) {
      onStepClick(index);
    }
  };

  return (
    <Box className={classes.stepperWrapper}>
      <Box className={classes.stepperContainer} ref={containerRef}>
        {steps.map((step, index) => (
          <Box
            key={step._id}
            className={classes.stepItem}
            ref={(el) => { stepRefs.current[index] = el; }}
          >
            <Box
              className={`${classes.stepCircle} ${getCircleClass(index)} ${isClickable(index) ? classes.clickable : ''}`}
              onClick={() => handleClick(index)}
            >
              <Typography variant='body02-bold' color='shades.clr-white-900'>
                {index + 1}
              </Typography>
            </Box>
            <Typography
              variant={index === currentStep ? 'h6-semiBold' : 'h6-medium'}
              color={index === currentStep ? 'primary.main' : index <= activeStep ? 'neutral.clr-900' : 'neutral.clr-400'}
              className={`${classes.stepLabel} ${isClickable(index) ? classes.clickableLabel : ''}`}
              onClick={() => handleClick(index)}
            >
              {step.label}
            </Typography>
            {index < steps.length - 1 && (
              <Box
                className={`${classes.connector} ${index < activeStep ? classes.completedConnector : ''}`}
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export { steps };
export default ResumeFormStepper;
